import React, { useMemo, useCallback } from 'react';
import { extractSvgGradients } from './svgUtils';

/**
 * Properties-panel section that exposes every <linearGradient> and
 * <radialGradient> defined inside an uploaded SVG and lets the user edit:
 *   • stop colors
 *   • stop offsets
 *   • stop opacities
 *   • linear-gradient angle
 *
 * Edits are persisted onto `element.gradientEdits[gradientId]` and
 * picked up by processSvgForRender's applyGradientEdits step.  The original
 * `svgContent` is never mutated — every render parses the source and applies
 * the edits on top.
 *
 * Designed to slot into the existing RightSidebar SVG block between the
 * "Colors" and "Stroke" sections.
 */

const ROUND2 = (n) => Math.round(n * 100) / 100;

// CSS preview for the gradient strip.  Approximate-but-faithful for inspection:
// honors stop offsets / colors / opacities and the linear angle, plus radial
// type.  The on-canvas render is still the real SVG — this is just a 16px tall
// thumbnail shown next to the gradient name.
const cssPreview = (g, stops) => {
  const pieces = (stops || []).map((s) => {
    const a = s.opacity == null ? 1 : s.opacity;
    const c = s.color || '#000';
    // Use rgba when opacity < 1 so the preview shows transparency the same way
    // the SVG does.  hex values are passed straight through to CSS otherwise.
    return `${a < 1 ? rgbaWithAlpha(c, a) : c} ${ROUND2(s.offset)}%`;
  });
  if (g.type === 'radial') {
    return `radial-gradient(circle at 50% 50%, ${pieces.join(', ')})`;
  }
  // Linear — CSS uses 0deg = up; SVG/math uses 0deg = right.  Convert.
  const cssAngle = (g.angle + 90) % 360;
  return `linear-gradient(${ROUND2(cssAngle)}deg, ${pieces.join(', ')})`;
};

// Hex → rgba string, tolerating short hex, longhand hex, and rgb()/rgba()
// inputs.  Anything we don't recognize is returned with opacity wrapped in
// a CSS color-mix so the preview still attempts something reasonable.
const rgbaWithAlpha = (c, a) => {
  if (typeof c !== 'string') return c;
  const s = c.trim();
  let m = s.match(/^#([0-9a-f]{3,8})$/i);
  if (m) {
    let h = m[1];
    if (h.length === 3) h = h.split('').map((x) => x + x).join('');
    if (h.length === 4) h = h.split('').map((x) => x + x).join('');
    const r = parseInt(h.slice(0, 2), 16);
    const g = parseInt(h.slice(2, 4), 16);
    const b = parseInt(h.slice(4, 6), 16);
    return `rgba(${r},${g},${b},${a})`;
  }
  m = s.match(/^rgba?\(([^)]+)\)$/i);
  if (m) {
    const parts = m[1].split(',').map((x) => x.trim());
    const [r, g, b] = parts;
    return `rgba(${r},${g},${b},${a})`;
  }
  // Fallback: leave the color, let CSS handle it as-is.
  return c;
};

/**
 * @param {object} props
 * @param {string} props.svgContent — element.svgContent (original source).
 * @param {object} props.gradientEdits — element.gradientEdits (or undefined).
 * @param {(edits: object) => void} props.onChange — fired with the full new
 *        gradientEdits map; caller routes it through updateElement().
 */
const SvgGradientsEditor = ({ svgContent, gradientEdits, onChange }) => {
  const gradients = useMemo(() => extractSvgGradients(svgContent), [svgContent]);

  // Build the per-gradient "effective" state: original parsed values overlaid
  // with any user edits.  This is what the panel displays AND what the
  // renderer's applyGradientEdits step receives.
  const effective = useMemo(() => {
    return gradients.map((g) => {
      const e = (gradientEdits && gradientEdits[g.id]) || {};
      return {
        ...g,
        stops: e.stops || g.stops,
        angle: Number.isFinite(e.angle) ? e.angle : g.angle,
      };
    });
  }, [gradients, gradientEdits]);

  const patchEdit = useCallback(
    (id, patch) => {
      const next = { ...(gradientEdits || {}) };
      const cur  = next[id] || {};
      next[id] = { ...cur, ...patch };
      onChange(next);
    },
    [gradientEdits, onChange],
  );

  // Reset one gradient's edits — caller can wipe the entry, restoring the
  // gradient to whatever the parsed source defined.
  const resetEdit = useCallback(
    (id) => {
      if (!gradientEdits) return;
      const next = { ...gradientEdits };
      delete next[id];
      onChange(next);
    },
    [gradientEdits, onChange],
  );

  if (gradients.length === 0) return null;

  return (
    <>
      <div className="flex items-center gap-2 py-2">
        <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
          Gradients
        </span>
        <div className="flex-1 h-px bg-gray-100" />
        <span className="text-[10px] text-gray-400">{gradients.length}</span>
      </div>

      <div className="space-y-3 pb-1">
        {effective.map((g) => {
          const stops = g.stops || [];
          const edited = !!(gradientEdits && gradientEdits[g.id]);
          const setStop = (idx, patch) => {
            const next = stops.map((s, i) => (i === idx ? { ...s, ...patch } : s));
            patchEdit(g.id, { stops: next });
          };

          return (
            <div
              key={g.id || `g-${Math.random()}`}
              className="rounded-md border border-gray-200 bg-white p-2.5 space-y-2"
              data-testid={`svg-gradient-${g.id}`}
            >
              {/* Header — id, type badge, preview, reset */}
              <div className="flex items-center gap-2 min-w-0">
                <span className="text-[11px] font-mono text-gray-700 truncate flex-1" title={g.id}>
                  #{g.id || '(no id)'}
                </span>
                <span
                  className={`text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                    g.type === 'linear'
                      ? 'bg-blue-50 text-blue-700'
                      : 'bg-purple-50 text-purple-700'
                  }`}
                >
                  {g.type}
                </span>
                {edited && (
                  <button
                    type="button"
                    onClick={() => resetEdit(g.id)}
                    className="text-[9px] text-gray-400 hover:text-purple-500 transition-colors"
                    title="Reset to original gradient"
                  >
                    Reset
                  </button>
                )}
              </div>

              {/* Visual preview strip */}
              <div
                className="h-5 rounded border border-gray-200"
                style={{ background: cssPreview(g, stops) }}
                title="Gradient preview (CSS approximation)"
              />

              {/* Metadata row — units / spreadMethod / gradientTransform */}
              {(g.gradientUnits || g.spreadMethod || g.gradientTransform) && (
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[9px] text-gray-400">
                  {g.gradientUnits     && <span>units: <span className="text-gray-600 font-mono">{g.gradientUnits}</span></span>}
                  {g.spreadMethod      && <span>spread: <span className="text-gray-600 font-mono">{g.spreadMethod}</span></span>}
                  {g.gradientTransform && <span title={g.gradientTransform}>transform: <span className="text-gray-600 font-mono truncate inline-block max-w-[140px] align-bottom">{g.gradientTransform}</span></span>}
                </div>
              )}

              {/* Linear angle */}
              {g.type === 'linear' && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide">Angle</span>
                    <span className="text-[10px] text-gray-500 font-mono">{ROUND2(g.angle)}°</span>
                  </div>
                  <input
                    type="range"
                    min={-180}
                    max={180}
                    step={1}
                    value={g.angle}
                    onChange={(e) => patchEdit(g.id, { angle: parseFloat(e.target.value) })}
                    className="w-full accent-purple-500"
                    data-testid={`gradient-angle-${g.id}`}
                  />
                </div>
              )}

              {/* Stops */}
              <div className="space-y-1.5">
                <span className="text-[10px] font-medium text-gray-500 uppercase tracking-wide block">
                  Stops · {stops.length}
                </span>
                {stops.map((s, i) => (
                  <div key={i} className="flex items-center gap-1.5 min-w-0">
                    <input
                      type="color"
                      value={normalizeHex(s.color)}
                      onChange={(e) => setStop(i, { color: e.target.value })}
                      className="w-7 h-7 rounded border cursor-pointer p-0.5 flex-shrink-0"
                      title="Stop color"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] text-gray-400">offset</span>
                        <span className="text-[9px] text-gray-500 font-mono">{ROUND2(s.offset)}%</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={0.1}
                        value={s.offset}
                        onChange={(e) => setStop(i, { offset: parseFloat(e.target.value) })}
                        className="w-full accent-purple-500 h-1"
                        title="Stop offset"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-[9px] text-gray-400">opacity</span>
                        <span className="text-[9px] text-gray-500 font-mono">{ROUND2(s.opacity)}</span>
                      </div>
                      <input
                        type="range"
                        min={0}
                        max={1}
                        step={0.01}
                        value={s.opacity}
                        onChange={(e) => setStop(i, { opacity: parseFloat(e.target.value) })}
                        className="w-full accent-purple-500 h-1"
                        title="Stop opacity"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

// The <input type="color"> picker requires a 6-digit hex.  SVG colors can be
// named ("gold"), short hex, rgb(), or rgba() — coerce to "#rrggbb" for the
// picker, leaving the underlying stored value untouched until the user moves
// the picker (at which point the picker writes back a clean 6-digit hex).
function normalizeHex(c) {
  if (typeof c !== 'string') return '#000000';
  const s = c.trim();
  if (/^#[0-9a-f]{6}$/i.test(s)) return s;
  if (/^#[0-9a-f]{3}$/i.test(s)) {
    const h = s.slice(1);
    return '#' + h.split('').map((x) => x + x).join('');
  }
  // Anything else (rgb(), named) — render an opaque-black swatch.  The user's
  // first interaction will replace it with a real hex.
  return '#000000';
}

export default SvgGradientsEditor;
